// App.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import ChatMessage from './components/ChatMessage';
// MessageInput 已改為 ChatRichTextarea（原生 textarea + 附件）
import LoginPage from './components/LoginPage';
import AuthErrorScreen from './components/AuthErrorScreen';
import MaintenanceScreen from './components/MaintenanceScreen';
import { subscribeMaintenance } from './utils/maintenance';
import PageHeader from './components/PageHeader';
import ScheduleView from './components/ScheduleView';
import MailNotificationView from './components/MailNotificationView';
import RichTextarea from './components/ChatRichTextarea';
import { useChat } from './hooks/useChat';
import { useChatHistoryWithAdvancedSync } from './hooks/useChatHistoryWithAdvancedSync';
import useAuth from './hooks/useAuth';
import { useStompClient } from './hooks/useStompClient';
import logger from './utils/logger';
import { trackEvent } from './utils/analytics';
import * as appStorage from './utils/appIndexedDB';
import apiService from './services/apiService';
import { OFFICIAL_SITE_PENDING_PROMPT_KEY } from './utils/officialSiteBridge';
import './App.css';
import { useCustomerServiceSubscription } from "./hooks/useCustomerServiceSubscription";
import { useWsMode } from "./hooks/useWsMode";
import { useTheme } from './hooks/useTheme';
import { getCustomerServiceSessionStatus, recordDeviceHeartbeat } from './utils/api';
import { isCurrentUserPcs } from './utils/pcsUser';

// 重新整理時要還原的主畫面（見 activeView）
const ACTIVE_VIEW_STORAGE_KEY = 'agui.activeView';
const RESTORABLE_VIEWS = ['schedule', 'mail-notification'];
// 推播深連結 payload 的 sessionStorage 暫存 key（change: notification-deeplink-chat-trigger）
// 未登入時點擊深連結，登入完成前先把 payload 存在這裡，撐過 SSO 整頁重新導向
const CHAT_LINK_PENDING_PAYLOAD_KEY = 'mygenie_pending_chatlink_payload';

const WELCOME_MESSAGES = [
  '嗨！準備好開始了嗎？',
  '你在做什麼？',
  '我們該從哪裡開始？',
  '我可以怎麼幫你？',
  '你今天在想什麼？',
];

function App() {
  const { theme, setTheme } = useTheme();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  // 主畫面顯示模式：'chat' 為原本聊天畫面，'schedule' 為建立排程畫面，'mail-notification' 為信件即時通知
  // 存在 sessionStorage：按 F5 留在原本的頁面，不會掉回上次選的對話（10-06）；新分頁一律從聊天開始
  const [activeView, setActiveView] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem(ACTIVE_VIEW_STORAGE_KEY);
      return RESTORABLE_VIEWS.includes(saved) ? saved : 'chat';
    } catch {
      return 'chat';
    }
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, activeView);
    } catch {
      // 無痕或封鎖儲存時就不記，重新整理回到聊天畫面
    }
  }, [activeView]);
  const [newChatStartersMountEl, setNewChatStartersMountEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // AGUI-1169：排程頁手機版排程清單抽屜的開關狀態，拉到這一層（而不是留在 ScheduleView
  // 自己的 local state）是因為 Sidebar 的全域選單開關按鈕也需要知道這個狀態——排程清單抽屜
  // 打開時，全域選單開關要跟著隱藏（兩個抽屜同時開會疊在一起），這件事單靠 ScheduleView
  // 自己管不到 Sidebar，所以狀態本身要放在兩者共同的父層。
  const [scheduleListOpen, setScheduleListOpen] = useState(false);
  // AGUI-1192：每次點選單的「信件即時通知」都遞增，讓頁面重新掛載、重抓狀態。
  // 已經在這頁時 setActiveView 同一個值不會重新渲染；手機版沒有訂閱按鈕，電腦版設定好之後只能靠這裡刷新。
  const [mailNotificationKey, setMailNotificationKey] = useState(0);
  const [isSmallChatMode, setIsSmallChatMode] = useState(false);  
  // 系統維護狀態：apiService 收到後端 maintenance:true 時由 utils/maintenance 通知，切換成維護畫面
  const [maintenance, setMaintenance] = useState({ active: false, message: '' });
  useEffect(() => subscribeMaintenance(setMaintenance), []);
  // 內外網檢查狀態
  const [isCheckingNetwork, setIsCheckingNetwork] = useState(true);
  const [isInternalNetwork, setIsInternalNetwork] = useState(null);
  const [networkError, setNetworkError] = useState(null);
  // 未登入錯誤畫面的實際原因用旗標（分流顯示用，見 AuthErrorScreen）；
  // 最終顯示哪個原因在 render 時依優先順序衍生計算，避免多個 effect 各自寫入同一個 state 造成時序疑慮
  const [mplusFailed, setMplusFailed] = useState(false);
  const [ssoFailed, setSsoFailed] = useState(false);
  // 登入狀態管理
  const auth = useAuth();
  // 解構出需要的值，確保依賴穩定
  const { isLoading: authIsLoading, isAuthenticated, logout, ssoLogin, mplusLogin, isMplusLoggingIn, sessionExpired } = auth;

  // 登入成功後清除先前殘留的失敗旗標，避免下次未登入時顯示舊訊息
  useEffect(() => {
    if (auth.user) {
      setMplusFailed(false);
      setSsoFailed(false);
    }
  }, [auth.user]);

  // 信件即時通知只開放 PCS domain（10-20）：非 PCS 不顯示選單，F5 還原到這頁也退回聊天
  const canUseMailNotification = useMemo(() => !!auth.user && isCurrentUserPcs(), [auth.user]);
  useEffect(() => {
    if (auth.user && !canUseMailNotification && activeView === 'mail-notification') {
      setActiveView('chat');
    }
  }, [auth.user, canUseMailNotification, activeView]);
  // 用於追蹤 SSO 登入是否已處理
  const ssoLoginProcessed = useRef(false);
  // 用於追蹤 M+ 登入是否已處理
  const mplusLoginProcessed = useRef(false);
  
  // 在組件初始化時同步檢查 URL 參數，如果檢測到 M+ 登入參數，立即設置狀態
  // 這樣可以避免在首次渲染時顯示登入頁面
  const checkMplusParams = () => {
    try {
      const url = new URL(window.location.href);
      const urlParams = new URLSearchParams(url.search);
      const auserid = urlParams.get('auserid');
      const token = urlParams.get('token');
      return !!(auserid && token);
    } catch (error) {
      return false;
    }
  };
  
  // 用於追蹤是否正在檢測 M+ 登入參數（在檢測到參數但尚未開始登入流程時使用）
  const [isCheckingMplusLogin, setIsCheckingMplusLogin] = useState(checkMplusParams());

  // 在組件初始化時同步檢查 URL hash 是否為 SSO 登入回呼，比照 checkMplusParams()
  // 避免在 SSO 交換 JWT 的 useEffect 還沒跑完前，畫面先落到「未登入」分支短暫顯示 AD 登入頁
  const checkSsoParams = () => {
    try {
      const hash = window.location.hash;
      if (!hash || !hash.includes('/ssologin')) {
        return false;
      }
      const hashParts = hash.split('?');
      if (hashParts.length < 2) {
        return false;
      }
      const urlParams = new URLSearchParams(hashParts[1]);
      return !!urlParams.get('sso_token');
    } catch (error) {
      return false;
    }
  };

  // 用於追蹤是否正在檢測/處理 SSO 登入（在偵測到 sso_token 但尚未完成交換前使用）
  const [isCheckingSsoLogin, setIsCheckingSsoLogin] = useState(checkSsoParams());

  // WebSocket
  const WS_ENDPOINT = process.env.REACT_APP_WS_ENDPOINT;
  const WS_SEND_DESTINATION =
    process.env.REACT_APP_WS_SEND_DEST || "/web/app/customer-service.sendMessage";
  // ✅ 1) 模式控制抽成 hook
  const { wsMode, wsEnabled, isHumanMode, tokenForWs, toggleTest, disableWs, enableHuman, enableAi, disableAi } = useWsMode({
    auth,
    wsEndpoint: WS_ENDPOINT,
  });
    // ✅ 2) 只開一條 stomp 連線
  const { client: stompClient, connected: wsConnected } = useStompClient(
    wsEnabled && tokenForWs ? WS_ENDPOINT : null,
    tokenForWs ? { Authorization: `Bearer ${tokenForWs}` } : {}
  );
  
  // 頁面重新整理後，查詢後端確認是否有活躍的客服 session，若有則恢復 human agent 模式
  useEffect(() => {
    if (!auth.user) return;

    const restoreHumanAgentSession = async () => {
      // [DIAG] 進入還原流程時，記錄當前連線條件
      logger.info('WebSocket[DIAG]', '還原流程啟動', {
        userId: auth.user?.userId ?? auth.user,
        hasToken: !!appStorage.getItemSync('mygenie_token'),
        wsEndpoint: process.env.REACT_APP_WS_ENDPOINT || '(未設定)',
        localFlag: appStorage.getItemSync('nowIsWebSocket'),
      });

      try {
        const status = await getCustomerServiceSessionStatus();
        // [DIAG] 記錄後端回傳的 session 狀態
        logger.info('WebSocket[DIAG]', '後端 session-status 回應', {
          active: status?.active,
          roomId: status?.roomId,
          rawStatus: JSON.stringify(status),
        });

        if (status?.active) {
          logger.info('WebSocket', '偵測到活躍的客服 session，恢復 human agent 模式', { roomId: status.roomId });
          enableHuman();
          // [DIAG] enableHuman 呼叫後，wsMode 需等到下一個 render 才更新，這裡只能記錄呼叫完成
          logger.info('WebSocket[DIAG]', 'enableHuman() 已呼叫，等待 wsMode 更新');
          await appStorage.setItem('nowIsWebSocket', 'true');
        } else {
          // 後端確認無活躍 session，清除本地旗標
          logger.info('WebSocket[DIAG]', '後端確認無活躍 session，設定 nowIsWebSocket=false');
          await appStorage.setItem('nowIsWebSocket', 'false');
        }
      } catch (error) {
        // API 呼叫失敗（網路中斷等），fallback 依 IndexedDB 旗標判斷
        logger.warn('WebSocket', '查詢客服 session 狀態失敗，改用本地旗標 fallback', {
          error: error?.message || error,
        });
        const savedFlag = appStorage.getItemSync('nowIsWebSocket') === 'true';
        if (savedFlag) {
          logger.info('WebSocket', 'Fallback: 依本地旗標恢復 human agent 模式');
          enableHuman();
        } else {
          await appStorage.setItem('nowIsWebSocket', 'false');
        }
      }
    };

    restoreHumanAgentSession();
  }, [auth.user, enableHuman]);

  // 裝置心跳：與客服 session 還原是各自獨立的關注點，故獨立成自己的 effect，
  // 避免耦合在一起（其中一個失敗不影響另一個）。涵蓋登入完成、或開啟網頁沿用既有 token 兩種情況。
  useEffect(() => {
    if (!auth.user) return;
    recordDeviceHeartbeat().catch((error) => {
      logger.warn('Device', '裝置心跳呼叫失敗，忽略（非關鍵路徑）', {
        error: error?.message || error,
      });
    });
  }, [auth.user]);

  // 聊天歷史管理（進階多裝置同步）
  // 传递 wsConnected 状态，以便在 WebSocket 连接时暂停送客语；wsEnabled 用於儲存到本地 isHuman（使用 WebSocket 時 true，否則 false）
  // isHumanMode 用於「15 分鐘無訊息」的忙線提示：那句話專屬轉專人流程，不可在 AI 對話（wsMode === 'ai'）出現
  const chatHistory = useChatHistoryWithAdvancedSync(auth, wsConnected, wsEnabled, isHumanMode);

  // 上傳前確保 backend session 已初始化（eager upload 在 sendMessage 之前執行，需提前建立 session）
  // 只向後端取得 chat_id，不在本地建立對話 entry（避免側欄在送訊息前就出現新對話）
  const ensureSessionPendingRef = useRef(null);
  const preallocatedServerChatIdRef = useRef(null);
  const ensureChatSession = useCallback(async () => {
    const currentId = chatHistory.currentChatId;
    if (currentId && !String(currentId).startsWith('temp-')) return currentId;
    if (ensureSessionPendingRef.current) return ensureSessionPendingRef.current;
    const promise = (async () => {
      try {
        const apiResponse = await apiService.createNewChatExternal();
        if (apiResponse === undefined) throw new Error('登入已過期');
        const serverChatId = apiResponse?.data?.data?.chat_id;
        if (!serverChatId) throw new Error('初始化聊天室失敗：缺少 chat_id');
        // 暫存後端 session id，等使用者真正送出訊息時 sendMessage 才建立本地對話（側欄才出現）
        preallocatedServerChatIdRef.current = serverChatId;
        return serverChatId;
      } finally {
        ensureSessionPendingRef.current = null;
      }
    })();
    ensureSessionPendingRef.current = promise;
    return promise;
  }, [chatHistory]);

  // 切換到已存在的真實對話時，清除暫存 id，避免跨對話汙染
  const currentChatId = chatHistory.currentChatId;
  useEffect(() => {
    if (currentChatId && !String(currentChatId).startsWith('temp-')) {
      preallocatedServerChatIdRef.current = null;
    }
  }, [currentChatId]);

  const consumePreallocatedChatId = useCallback(() => {
    const id = preallocatedServerChatIdRef.current;
    preallocatedServerChatIdRef.current = null;
    return id;
  }, []);
  // useChat（下方宣告）才會提供實際的串流事件處理函式；
  // 這裡先用 ref 橋接，避免調整 useChat/useCustomerServiceSubscription 既有的 hook 呼叫順序
  const aiStreamEventHandlerRef = useRef(() => {});
  // ✅ 3) 只訂閱一次，human 模式時自動打標
  useCustomerServiceSubscription({
    enabled: wsEnabled,
    stompClient,
    connected: wsConnected,
    authUser: auth.user,
    chatHistory,
    isHumanAgent: isHumanMode,
    disableWs,
    onAiStreamEvent: (event) => aiStreamEventHandlerRef.current(event),
  });

  // 判斷是否為新聊天（只有歡迎訊息或沒有用戶訊息）
  // 基於 chatHistory 的當前訊息來判斷，避免循環依賴
  const currentMessages = chatHistory.getCurrentMessages();
  const isNewChat = currentMessages.length === 0 || 
    (currentMessages.length === 1 && (currentMessages[0].sender === 'chatbot' || currentMessages[0].sender === 'rocketChat')) ||
    !currentMessages.some(msg => msg.sender === 'guest');

  
  // 提取名字（中文去姓，英文等直接使用）
  const getFirstName = (username) => {
    if (!username) return '';
    const trimmed = username.trim();
    // 如果包含空格，取第一個部分（當作 first name）
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(/\s+/);
      return parts[0];
    }
    // 僅對可能為中文名（含 CJK 字元）時才去掉第一個字當作姓
    const hasCJK = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/.test(trimmed);
    if (hasCJK && trimmed.length > 1) {
      return trimmed.substring(1);
    }
    // 英文名或單字：直接使用全名
    return trimmed;
  };

  const displayName = auth.user?.displayName ? getFirstName(auth.user.displayName) : '';

  // 首次登入（或旗標為 false）時，強制使用淺色主題並更新旗標
  useEffect(() => {
    if (!auth.user || typeof window === 'undefined') return;

    const isNotFirstLogin = window.localStorage.getItem('isNotFirstLogin');
    if (isNotFirstLogin !== 'true') {
      setTheme('light');
      window.localStorage.setItem('isNotFirstLogin', 'true');
    }
  }, [auth.user, setTheme]);

  // 登入後預先抓取倒讚快速選項
  const [feedbackQuickOptions, setFeedbackQuickOptions] = useState([]);
  useEffect(() => {
    if (!auth.user) return;
    apiService.getSysParams('feedback_quick_option', 'content')
      .then(data => setFeedbackQuickOptions(data))
      .catch(() => {});
  }, [auth.user]);

  
  // 聊天功能
  const { messages, input, setInput, isLoading, isStopping, isAwaitingReply, sendMessage, resendMessage, stopGeneration, messagesEndRef, messagesContainerRef, lastBotMessageRef, lastUserMessageRef, bottomSpacerRef, isFinalizeTransient, isPositioning, scrollToBottom, onAiStreamEvent } = useChat(
    chatHistory,
    chatHistory.updateChatMessages,
    chatHistory.getCurrentMessages,
    {
      stompClient,
      wsConnected,
      wsSendDestination: WS_SEND_DESTINATION,
      isHumanMode,
      enableAi,
      disableAi,
      isNewChat,
      createNewChat: chatHistory.createNewChat,
      consumePreallocatedChatId,
      isMobile,
      // 供 useChat 的載入補收掃描判斷「token 是否已到位」（見 useChat.js 觸發點 1）：
      // M+ 進站流程是「先登出再登入」，useChat 是無條件呼叫的 hook（上方 early return 只擋畫面、
      // 擋不住 effect），若不帶這個旗標，載入掃描會在登出後的空窗期就把唯一一次掃描機會用掉。
      isAuthed: auth.isAuthenticated(),
      // 認證流程進行中（M+ 的「先登出再登入」尚未走完）：此期間 isAuthed 可能是 checkAuthStatus
      // 從 IndexedDB 還原的「上一個 session」，不是這次進站的真正登入狀態，載入掃描不可據此搶跑。
      // isCheckingMplusLogin 在 mount 時就由 URL 參數同步決定（見上方 useState 初始值），
      // 因此第一個 render 就能擋住，不會有「先掃了才知道是 M+ 進站」的空窗。
      authTransitioning: isCheckingMplusLogin || isMplusLoggingIn,
    }
  );

  // 最新一則使用者訊息的索引：串流時 messages 陣列尾端是暫時 bot 泡泡（見 useChat.js），
  // 不能用 idx===messages.length-1 判斷，需另外找「最後一個 sender==='guest'」的訊息掛 ref
  const lastGuestMessageIdx = useMemo(
    () => messages.reduce((acc, m, i) => (m.sender === 'guest' ? i : acc), -1),
    [messages]
  );

  // MCP Apps 互動畫面要求以使用者身分送出訊息（ui/message，AGUI-1274）。
  // 畫面只等 10 秒，而 sendMessage 會 await 到 AI 回覆落地，故不等它結束：
  // 以 onDispatched 得知「提問已放進對話並開始送出」就立刻回成功；目前對話仍在等回覆（同一 GenAIus
  // session 一次只處理一題）或 sendMessage 任一守衛放棄時回失敗，畫面會顯示「請複製文字自行送出」。
  // 最新的 sendMessage／等待狀態放 ref，讓這個 handler 身分固定，不因串流中的 re-render 而變動。
  const mcpAppSendRef = useRef({ sendMessage, isLoading, isAwaitingReply });
  mcpAppSendRef.current = { sendMessage, isLoading, isAwaitingReply };
  const handleMcpAppSendMessage = useCallback((text) => new Promise((resolve) => {
    const { sendMessage: send, isLoading: loading, isAwaitingReply: awaiting } = mcpAppSendRef.current;
    if (loading || awaiting) {
      resolve(false);
      return;
    }
    let settled = false;
    const settle = (ok) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    // preserveInput：訊息不是從輸入框來的，不清掉使用者打到一半的草稿
    Promise.resolve()
      .then(() => send(text, { onDispatched: settle, preserveInput: true }))
      .catch((err) => {
        console.warn('[MCP App] 代送訊息失敗', err);
      })
      // 已在 onDispatched 回報過結果時這裡是 no-op；沒回報過（例外中斷）則視為沒送出
      .finally(() => settle(false));
  }), []);

  // 讓 useCustomerServiceSubscription（在上方先呼叫）能透過 ref 橋接呼叫到這裡才產生的最新處理函式
  useEffect(() => {
    aiStreamEventHandlerRef.current = onAiStreamEvent;
  }, [onAiStreamEvent]);

  // 統一的新對話處理：建立新對話並清空輸入框
  const handleCreateNewChat = useCallback(() => {
    setActiveView('chat');
    chatHistory.createNewChat();
    setInput('');
  }, [chatHistory.createNewChat, setInput]);

  // 側欄點擊既有對話時，切回聊天畫面
  const handleSwitchToChat = useCallback((chatId) => {
    setActiveView('chat');
    chatHistory.switchToChat(chatId);
  }, [chatHistory.switchToChat]);

  // 取消轉專人：關閉 WebSocket 連線（不論目前是 test 或 human 模式都關閉）
  const handleCancelTransfer = useCallback(() => {
    disableWs();
  }, [disableWs]);

  // 進站前處理：清除 externalSendMessage_ 開頭的除錯紀錄（sendMessageExternal 請求/回應日誌），避免 IndexedDB 無限累積
  useEffect(() => {
    appStorage.removeItemsByPrefix('externalSendMessage_')
      .then((count) => {
        if (count > 0) {
          logger.info('STORAGE', `已清除 ${count} 筆 externalSendMessage_ 紀錄`);
        }
      })
      .catch((error) => {
        logger.warn('STORAGE', '清除 externalSendMessage_ 紀錄失敗', { error: error?.message || error });
      });
  }, []);

  // 檢查用戶是否為內網
  useEffect(() => {
    const checkClientNetwork = async () => {
      try {
        setIsCheckingNetwork(true);
        setNetworkError(null);

        // 使用 apiService 會打到 REACT_APP_API_BASE_URL（例如 http://localhost:8097/bff）
        const data = await apiService.request('/client/network');
        logger.info('NETWORK', '收到 client network 回應', data);

        // 只允許 internal=true 的用戶看到登入頁
        setIsInternalNetwork(!!data.internal);
      } catch (error) {
        logger.error('NETWORK', '檢查用戶網路來源失敗', { error: error?.message || error });
        setNetworkError('無法確認網路來源，請稍後再試或聯絡系統管理員。');
        // 安全起見，視為外網（不顯示登入頁）
        setIsInternalNetwork(false);
      } finally {
        setIsCheckingNetwork(false);
      }
    };

    checkClientNetwork();
  }, []);

  // 官網等系統以 window.open 開出本頁時，內網且尚未登入直接自動觸發 SSO，不顯示手動登入頁
  // （AGUI-1175）。只在有 window.opener（代表本頁是被其他視窗開出來的）時才生效；一般直接
  // 造訪、外網、M+（這幾種情境本來就不會有 window.opener）完全不受影響，沿用原本各自的
  // 登入頁／錯誤畫面邏輯，避免「不管三七二十一都做SSO」波及外網使用者或 M+ 既有流程。
  // 追蹤「這個分頁曾經登入成功過」，跟「這次載入還沒登入過、正在跑自動SSO」區分開來。
  // 使用者主動登出後 isAuthenticated() 一樣會變回 false，但 window.opener／內網／ssoFailed
  // 這幾個條件都沒變，若不排除會被誤判成「尚未登入，該自動觸發SSO」，卡在下面第 830 行附近
  // 那段「登入中...」畫面出不去——不是真的在重試登入，是畫面判斷邏輯沒把「主動登出」納入
  // 考量（sit 實測重現：只有透過官網彈窗、也就是有 window.opener 的情境才會卡，一般 SSO
  // 登入沒有 window.opener，不會走到這段判斷，所以不受影響）。
  const hasEverAuthenticatedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && isAuthenticated()) {
      hasEverAuthenticatedRef.current = true;
    }
  }, [isAuthenticated]);

  // 是否帶著推播深連結（AGUI-1161）：網址 hash 當下就帶 #/chatlink，或先前已暫存到
  // sessionStorage（見下方深連結偵測 effect）。跟 window.opener 並列作為自動觸發 SSO 的條件，
  // 因為 email/M+ 等管道點開深連結通常是全新分頁，不會有 window.opener。
  const hasChatLinkTrigger = () => {
    if ((window.location.hash || '').includes('/chatlink')) return true;
    try {
      return !!window.sessionStorage.getItem(CHAT_LINK_PENDING_PAYLOAD_KEY);
    } catch (error) {
      return false;
    }
  };

  const autoSsoTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoSsoTriggeredRef.current) return;
    if (authIsLoading) return;
    // 這個分頁曾經登入過又變回未登入：代表使用者主動登出，不是「還沒登入過」，不要自動
    // 再導去 SSO（見上方 hasEverAuthenticatedRef 說明）。
    if (hasEverAuthenticatedRef.current) return;
    // SSO 登入交換 JWT 進行中（或這次載入本身就是 SSO 回呼）時一律不觸發，否則會誤判成
    // 「尚未登入」又導一次 SSO，把還沒跑完的交換打斷（sit 實測重現：getUserInfo 每次都
    // 200 成功換到 JWT，但都被下一輪 autoLogin 蓋掉，每輪 tokenId 都不同、無限重導）。
    //
    // 只檢查網址 hash 不夠：handleSSOLogin 是「先同步清掉 hash，才開始 await 非同步交換」，
    // 也就是 hash 在交換『開始前』就已經被清空，不是『結束後』——真正在等待交換結果的這段
    // 期間，hash 早就不含 /ssologin 了。若這裡的 /bff/client/network 剛好比交換晚 resolve，
    // 會在 hash 已清空、JWT 還沒換到的空窗期被誤判成「尚未登入」而重新觸發。
    //
    // 改用 ssoLoginProcessed 這個 ref：它在 handleSSOLogin 清 hash 的『同一行』就同步設為
    // true，且整個頁面生命週期內不會被重置——不像 hash 字串會被清掉，也不依賴後續非同步
    // 交換是否已經完成，可以正確涵蓋「已開始處理但還沒交換完」這整段期間。兩個判斷都留著：
    // hash 檢查擋住 handleSSOLogin 這個 effect 本身還沒跑到（宣告順序在後）的第一個 render；
    // ref 檢查擋住 hash 清空之後、交換完成之前的所有後續 render。
    const hash = window.location.hash || '';
    if (hash.includes('/ssologin')) return;
    if (ssoLoginProcessed.current) return;
    if (isCheckingMplusLogin || isMplusLoggingIn) return;
    if (isAuthenticated && isAuthenticated()) return;
    // SSO 交換已經跑過且失敗（例如公司 SSO 系統本身拒絕）：不要無限重試，讓使用者落到
    // 下方一般登入頁，可以看到失敗訊息、自行改用其他登入方式或重新整理再試一次。
    if (ssoFailed) return;
    if (isInternalNetwork !== true) return;
    // window.opener（官網等系統彈窗開出，AGUI-1175）或帶著推播深連結（AGUI-1161，email/M+
    // 等管道點進來的新分頁通常不會有 window.opener）任一成立即可，兩種情境都不該卡在手動登入頁。
    if (!window.opener && !hasChatLinkTrigger()) return;
    const ssoTarget = process.env.REACT_APP_SSO_TARGET;
    if (!ssoTarget) return;

    autoSsoTriggeredRef.current = true;
    logger.auth('偵測到由其他視窗開出或帶推播深連結且尚未登入，自動觸發 SSO', { target: ssoTarget });
    window.location.href = ssoTarget;
  }, [authIsLoading, isAuthenticated, isInternalNetwork, isCheckingMplusLogin, isMplusLoggingIn, ssoFailed]);

  // 檢測 SSO 登入 URL
  useEffect(() => {
    // 如果已經處理過 SSO 登入，則跳過
    if (ssoLoginProcessed.current) {
      return;
    }
    
    // 等待認證狀態檢查完成後再處理 SSO 登入
    if (authIsLoading) {
      return;
    }
    
    // 確保 auth 對象的方法已初始化
    if (!isAuthenticated || !logout || !ssoLogin) {
      return;
    }
    
    const handleSSOLogin = async () => {
      // 檢查 URL hash 是否包含 SSO 登入參數
      // URL 格式: https://localhost:3000/#/ssologin?sso_token=xxxxxxxxx
      try {
        const hash = window.location.hash;
        if (hash && hash.includes('/ssologin')) {
          // 標記為已處理，避免重複執行
          ssoLoginProcessed.current = true;

          // 從 hash 中提取查詢參數
          // hash 格式: #/ssologin?sso_token=xxxxxxxxx
          const hashParts = hash.split('?');
          if (hashParts.length > 1) {
            const urlParams = new URLSearchParams(hashParts[1]);
            const ssoToken = urlParams.get('sso_token');

            if (ssoToken) {
              logger.auth('檢測到 SSO 登入請求', { hasToken: !!ssoToken });

              // 清除 URL hash，避免重複處理
              window.history.replaceState(null, '', window.location.pathname + window.location.search);

              // 如果已經登入，先登出
              if (isAuthenticated()) {
                logger.auth('已登入，先執行登出');
                await logout();
              }

              // 執行 SSO 登入
              const result = await ssoLogin(ssoToken);

              if (!result.success) {
                logger.error('AUTH', 'SSO 登入失敗', { message: result.message });
                setSsoFailed(true);
              }
            } else {
              logger.warn('AUTH', 'SSO 登入 URL 缺少 token 參數');
            }
          } else {
            logger.warn('AUTH', 'SSO 登入 URL 格式不正確');
          }
        }
      } finally {
        // 不論成功、失敗或格式不正確，都要解除「登入中」畫面阻擋，
        // 否則 isCheckingSsoLogin 初值為 true 時使用者會卡在載入畫面出不去
        setIsCheckingSsoLogin(false);
      }
    };

    handleSSOLogin();
  }, [authIsLoading, isAuthenticated, logout, ssoLogin]); // 使用解構的值，確保依賴穩定

  // 檢測 M+ 登入 URL queryString 參數
  useEffect(() => {
    // 如果已經處理過 M+ 登入，則跳過
    if (mplusLoginProcessed.current) {
      return;
    }
    
    // 等待認證狀態檢查完成後再處理 M+ 登入
    if (authIsLoading) {
      return;
    }
    
    // 確保 auth 對象的方法已初始化
    if (!mplusLogin) {
      return;
    }
    
    // 同步檢查 URL 參數，如果檢測到 M+ 登入參數，立即設置狀態
    // 這樣可以避免在檢測完成前顯示登入頁面
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(url.search);
    const auserid = urlParams.get('auserid');
    const token = urlParams.get('token');
    
    // 如果檢測到 M+ 登入參數，立即設置檢測狀態
    if (auserid && token) {
      setIsCheckingMplusLogin(true);
    }
    
    const handleMplusLogin = async () => {
      
      // 根據 pathname 決定要使用的 M+ API 端點
      let apiEndpoint = '/mplus/login'; // 預設值
      const pathname = url.pathname;
      if (pathname === '/mplus/twm') {
        apiEndpoint = '/web/mplus/login';
      } else if (pathname === '/mplus/store') {
        apiEndpoint = '/web/mplus/login/store';
      } else if (pathname === '/mplus/dealer') {
        apiEndpoint = '/web/mplus/login/dealer';
      }
      
      // 必須同時有 auserid 和 token 兩個參數才會走 M+ 登入流程
      if (auserid && token) {
        // 標記為已處理，避免重複執行
        mplusLoginProcessed.current = true;
        
        logger.auth('檢測到 M+ 登入請求', { 
          auserid, 
          hasToken: !!token,
          pathname,
          apiEndpoint
        });
        
        // 清除 URL queryString，避免重複處理
        window.history.replaceState(null, '', window.location.pathname);
        
        // 如果已經登入，先登出
        if (isAuthenticated()) {
          logger.auth('已登入，先執行登出');
          await logout();
        }
        
        // 執行 M+ 登入，傳入 API 端點
        const result = await mplusLogin(auserid, token, apiEndpoint);
        
        if (!result.success) {
          logger.error('AUTH', 'M+ 登入失敗', { message: result.message });
          // 登入失敗時，清除檢測狀態，顯示登入頁面
          setIsCheckingMplusLogin(false);
          setMplusFailed(true);
        } else {
          // 登入成功時，清除檢測狀態（isMplusLoggingIn 會在 mplusLogin 中處理）
          setIsCheckingMplusLogin(false);
        }
      } else {
        // 缺少必要參數，記錄警告但不執行登入
        if (auserid || token) {
          logger.warn('AUTH', 'M+ 登入 URL 缺少必要參數', {
            hasAuserid: !!auserid,
            hasToken: !!token
          });
        }
        // 確保檢測狀態為 false
        setIsCheckingMplusLogin(false);
      }
    };
    
    handleMplusLogin();
  }, [authIsLoading, isAuthenticated, logout, mplusLogin]); // 使用解構的值，確保依賴穩定

  // 推播深連結（change: notification-deeplink-chat-trigger）：解密＋送出共用邏輯，
  // 供下方「已登入時立即消費」與「暫存後登入完成再消費」兩條路徑共用，避免邏輯分岔。
  const consumeAndTriggerChatLink = async (payload) => {
    try {
      logger.auth('開始消費推播深連結');
      const data = await apiService.consumeChatLink(payload);
      // 後端只做解密驗證、回傳 { prompt, channel }，不建立對話、不呼叫 GenAIus：
      // 「建立新對話＋送出 prompt」重用既有 sendMessage（跟手動打字送出同一條路徑），
      // forceNewChat 強制開新對話（不管目前開著哪個對話），sourceChannel 一路帶到後端
      // 記錄對話來源管道。這樣畫面會自然呈現「已送出、等待回覆中」的既有 UX，
      // 而不是等整個 AI 回應產生完才一次顯示問題與答案。
      sendMessage(data.prompt, { forceNewChat: true, sourceChannel: data.channel });
      // GTM 追蹤（change: gtm-deeplink-click-tracking）：sendMessage 不 await，故事件語意是「已觸發送出」
      // 而非已送達/已回覆；只送 channel，不送 prompt 內容。放在 consume 成功之後，失敗路徑不會記錄。
      trackEvent('deeplink_send_triggered', { channel: data.channel });
      logger.success('聊天', '推播深連結已觸發送出', { channel: data.channel });
    } catch (error) {
      logger.error('聊天', '消費推播深連結失敗', { error: error?.message });
    }
  };

  // 推播深連結（change: notification-deeplink-chat-trigger）：sessionStorage 暫存 key，
  // 比照既有 sso_token/auserid 兩個 pattern 的處理方式；值放在 module 層級無關 state，不需要放進依賴陣列
  // 一偵測到 #/chatlink?payload=... 就先暫存到 sessionStorage 並清除 URL，避免使用者尚未登入時
  // 觸發的 SSO 整頁重新導向（sso.html → 企業 SSO → getTokenId → #/ssologin）把這個參數弄丟；
  // sessionStorage 在同一分頁的跨 origin 導向過程中會被保留下來。
  //
  // 用 ref 存「當下最新」版本的處理函式，監聽器本身只在掛載時註冊一次（見下方 useEffect），
  // 而不是把 authIsLoading/isAuthenticated/chatHistory 等值放進 useCallback 依賴陣列後每次
  // 變動就整個重新註冊監聽器——那樣容易在監聽器還沒重新註冊完成前，用到舊版閉包（跟
  // createNewChat 疊加舊 chatHistory 把歷史蓋掉是同一類問題）。這個 ref 每次 render 都會
  // 重新賦值，確保無論監聽器何時被觸發，執行的永遠是「當下」最新的判斷邏輯與 closure。
  const handleChatLinkHashRef = useRef(null);
  handleChatLinkHashRef.current = () => {
    const hash = window.location.hash;
    if (!hash || !hash.includes('/chatlink')) {
      return;
    }
    const hashParts = hash.split('?');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (hashParts.length <= 1) return;
    const urlParams = new URLSearchParams(hashParts[1]);
    const payload = urlParams.get('payload');
    if (!payload) return;

    // 已登入且聊天歷史已載入完成：不透過 sessionStorage 繞一圈，直接消費。
    // 供使用者原本就開著 MyBro 分頁、貼上新連結（同頁 hash 變化，不會整頁重新載入、
    // 不會重新掛載元件）的情境使用；只掛載一次的 effect 抓不到這種同頁 hash 變化。
    const readyNow = !authIsLoading && isAuthenticated && isAuthenticated() && chatHistory.isInitialized;
    if (readyNow) {
      logger.auth('偵測到推播深連結（已登入），直接消費');
      consumeAndTriggerChatLink(payload);
      return;
    }
    try {
      window.sessionStorage.setItem(CHAT_LINK_PENDING_PAYLOAD_KEY, payload);
      logger.auth('偵測到推播深連結，已暫存待登入完成後自動接續');
    } catch (error) {
      logger.warn('AUTH', '暫存推播深連結 payload 失敗', { error: error?.message });
    }
  };

  useEffect(() => {
    handleChatLinkHashRef.current(); // 處理整頁載入當下就帶著 hash 的情況（新分頁/整頁重新載入）
    const listener = () => handleChatLinkHashRef.current();
    // hashchange：處理「同一分頁貼上新連結」的情況（只有 hash 不同，瀏覽器不會整頁重新載入，
    // 元件不會重新掛載，只掛載一次的 effect 抓不到）
    window.addEventListener('hashchange', listener);
    return () => window.removeEventListener('hashchange', listener);
  }, []);

  // 用於追蹤推播深連結是否已消費（避免重新整理/effect 重跑時重複觸發）
  const chatLinkProcessedRef = useRef(false);

  // 已登入後（不論是 builtin/LDAP 頁內登入，或 SSO 整頁繞一圈重新載入後），
  // 檢查 sessionStorage 是否有待處理的深連結 payload，有就自動接續執行「建立對話＋送出 prompt」，
  // 使用者不需要重新點擊原始連結（change: notification-deeplink-chat-trigger）
  useEffect(() => {
    if (chatLinkProcessedRef.current) return;
    if (authIsLoading) return;
    if (!isAuthenticated || !isAuthenticated()) return;
    // 必須等登入後的聊天歷史載入完成才能動作：createNewChat 是以「目前 chatHistory 閉包」
    // 疊加一筆新對話後整包 setChatHistory 覆寫回去，若在歷史尚未載入完成前就呼叫，
    // 疊加的基底會是載入前的舊值（通常是空陣列），載入完成的那次 setChatHistory 一旦先送出，
    // 會被這裡疊加自舊值的結果蓋掉，造成側欄歷史對話消失。
    if (!chatHistory.isInitialized) return;

    let pendingPayload = null;
    try {
      pendingPayload = window.sessionStorage.getItem(CHAT_LINK_PENDING_PAYLOAD_KEY);
    } catch (error) {
      return;
    }
    if (!pendingPayload) return;

    chatLinkProcessedRef.current = true;
    try {
      window.sessionStorage.removeItem(CHAT_LINK_PENDING_PAYLOAD_KEY);
    } catch (error) {
      // 移除失敗不影響本次消費，僅可能導致下次重新整理時再次誤觸發，風險低
    }

    consumeAndTriggerChatLink(pendingPayload);
    // sendMessage 透過閉包直接取用最新版本即可；刻意不放進依賴陣列，避免每次重新產生的
    // function 參照都重新觸發本 effect——本 effect 是否執行已由上方的 chatLinkProcessedRef
    // 一次性 guard 住，不依賴這裡的依賴陣列判斷是否重跑。isInitialized 則必須放進依賴陣列：
    // 若登入當下歷史尚未載入完成而提早 return，之後歷史載入完成時要能重新觸發本 effect。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authIsLoading, isAuthenticated, chatHistory.isInitialized]);

  // 官網跨系統帶入問題（AGUI-1175）：postMessage 交握（officialSiteBridge.js）收到內容後
  // 直接寫明文到 sessionStorage，不像推播深連結需要先呼叫後端解密——內容本來就是官網用
  // postMessage 現場塞進來的明文，不經過 URL／後端。這裡沿用跟上方深連結完全相同的
  // 「登入完成＋聊天歷史載入完成才消費」guard 邏輯，只是直接呼叫 sendMessage，不做解密。
  const officialSitePromptProcessedRef = useRef(false);
  useEffect(() => {
    if (officialSitePromptProcessedRef.current) return;
    if (authIsLoading) return;
    if (!isAuthenticated || !isAuthenticated()) return;
    if (!chatHistory.isInitialized) return;

    let pendingPrompt = null;
    try {
      pendingPrompt = window.sessionStorage.getItem(OFFICIAL_SITE_PENDING_PROMPT_KEY);
    } catch (error) {
      return;
    }
    if (!pendingPrompt) return;

    officialSitePromptProcessedRef.current = true;
    try {
      window.sessionStorage.removeItem(OFFICIAL_SITE_PENDING_PROMPT_KEY);
    } catch (error) {
      // 移除失敗不影響本次消費，僅可能導致下次重新整理時再次誤觸發，風險低
    }

    logger.auth('偵測到官網帶入的問題，自動送出');
    sendMessage(pendingPrompt, { forceNewChat: true, sourceChannel: 'website' });
    // GTM 追蹤（change: gtm-deeplink-click-tracking）：官網導流的送出次數，不送 prompt 內容
    trackEvent('website_prompt_send', { channel: 'website' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authIsLoading, isAuthenticated, chatHistory.isInitialized]);

  // 檢測是否為小視窗（手機端或視窗過小）
  // 只在跨越斷點時才重設側邊欄：手機彈出虛擬鍵盤（例如點側邊欄搜尋框）也會觸發 resize，
  // 若每次都重設會把使用者剛打開的側邊欄收回去（AGUI-1250）
  useEffect(() => {
    let prevIsSmallScreen = null;
    const checkIsMobile = () => {
      const isSmallScreen = window.innerWidth <= 820;
      if (isSmallScreen === prevIsSmallScreen) return;
      prevIsSmallScreen = isSmallScreen;
      setIsMobile(isSmallScreen);
      if (isSmallScreen) {
        setSidebarOpen(false);
        setMobileMenuOpen(false);
      } else {
        setSidebarOpen(true);
        setMobileMenuOpen(false);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // 檢測 chat-container 寬度，小於 500px 時切換到小屏幕模式
  useEffect(() => {
    const checkChatContainerWidth = () => {
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        const width = chatContainer.offsetWidth;
        setIsSmallChatMode(width < 500);
      }
    };

    // 初始檢測
    checkChatContainerWidth();

    // 使用 ResizeObserver 監聽 chat-container 寬度變化
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      const resizeObserver = new ResizeObserver(checkChatContainerWidth);
      resizeObserver.observe(chatContainer);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // 鍵盤快捷鍵：Ctrl+Shift+O 創建新聊天
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 檢查是否按下 Ctrl+Shift+O
      if (e.ctrlKey && e.shiftKey && e.key === 'O') {
          e.preventDefault();
          handleCreateNewChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCreateNewChat]);

  // 依本地儲存的 nowIsWebSocket 判斷目前是否處於 WebSocket 模式
  let isNowWebSocket = false;
  try {
    if (typeof window !== 'undefined') {
      isNowWebSocket = appStorage.getItemSync('nowIsWebSocket') === 'true';
    }
  } catch (error) {
    isNowWebSocket = false;
  }

  // 不再自動添加歡迎訊息，改為在輸入框上方顯示提示文字

  // 系統維護中：優先於其他畫面（登入頁、載入中、主畫面）顯示維護畫面
  if (maintenance.active) {
    return <MaintenanceScreen message={maintenance.message} />;
  }

  // 如果正在載入登入狀態或檢查內外網，顯示載入畫面
  if (auth.isLoading || isCheckingNetwork) {
    return (
      <div className="app">
        <div className="app-loading-screen">
          載入中...
        </div>
      </div>
    );
  }

  // 如果正在檢測或處理 M+／SSO 登入，顯示載入畫面（避免顯示登入頁面）
  if (isCheckingMplusLogin || isMplusLoggingIn || isCheckingSsoLogin) {
    return (
      <div className="app">
        <div className="app-loading-screen">
          登入中...
        </div>
      </div>
    );
  }

  // 如果未登入，根據內外網決定顯示登入頁或錯誤訊息
  if (!auth.isAuthenticated()) {
    // 檢查結果為外網或發生錯誤，不顯示登入頁，直接顯示錯誤訊息（依實際原因分流）
    if (isInternalNetwork === false) {
      // 依優先順序衍生出最終原因：network 檢查本身失敗 > M+ 失敗 > SSO 失敗 > 登入逾時 > 純外網訪問（預設）
      // 用 render 當下的旗標算出來，不靠多個 effect 互相搶著寫同一個 state，沒有時序問題
      const reason = networkError
        ? 'network-check-failed'
        : mplusFailed
        ? 'mplus-failed'
        : ssoFailed
        ? 'sso-failed'
        : sessionExpired
        ? 'session-expired'
        : 'external';
      return <AuthErrorScreen reason={reason} />;
    }

    // 由其他視窗開出（例如官網 popup，AGUI-1175）或帶著推播深連結（AGUI-1161）且內網、
    // 尚未登入：上方 effect 會自動觸發 SSO 導轉，這裡先顯示載入畫面，避免使用者看到
    // 一閃而過的手動登入頁。
    // ssoFailed 已排除：SSO 交換跑過且失敗時落到下方一般登入頁，不要卡在載入畫面出不去。
    // hasEverAuthenticatedRef 已排除：曾經登入過（代表這次是主動登出，不是還沒登入過）
    // 也要落到下方一般登入頁，不然登出後會一直卡在這個「登入中...」畫面出不來。
    if ((window.opener || hasChatLinkTrigger()) && isInternalNetwork === true && process.env.REACT_APP_SSO_TARGET
        && !ssoFailed && !hasEverAuthenticatedRef.current) {
      return (
        <div className="app">
          <div className="app-loading-screen">
            登入中...
          </div>
        </div>
      );
    }

    // 內網使用者才會看到登入頁
    return <LoginPage onLogin={auth.login} />;
  }

  // 已登入，顯示主應用程式
  // WebSocket 接通時隱藏 Sidebar，斷開時才顯示
  return (
    <div className="app">
      {!isNowWebSocket && (
        <Sidebar 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          chatHistory={chatHistory.chatHistory}
          createNewChat={handleCreateNewChat}
          switchToChat={handleSwitchToChat}
          deleteChat={chatHistory.deleteChat}
          currentChatId={chatHistory.currentChatId}
          user={auth.user}
          onLogout={auth.logout}
          openNewChat={chatHistory.openNewChat}
          theme={theme}
          setTheme={setTheme}
          activeView={activeView}
          scheduleListOpen={scheduleListOpen}
          onOpenSchedule={() => {
            setActiveView('schedule');
            // AGUI-1169：手機版每次點「排程管理」進來都預設展開排程清單抽屜。
            setScheduleListOpen(isMobile);
          }}
          onOpenMailNotification={() => {
            setActiveView('mail-notification');
            setMailNotificationKey((k) => k + 1);
          }}
          showMailNotification={canUseMailNotification}
        />
      )}

      {activeView === 'mail-notification' && canUseMailNotification ? (
        <div className="chat-container schedule-container">
          <MailNotificationView key={mailNotificationKey} isMobile={isMobile} />
        </div>
      ) : activeView === 'schedule' ? (
        <div className="chat-container schedule-container">
          <ScheduleView
            isMobile={isMobile}
            mobileListOpen={scheduleListOpen}
            setMobileListOpen={setScheduleListOpen}
          />
        </div>
      ) : (
      <div className={`chat-container ${isNewChat ? 'new-chat-mode' : ''} ${isSmallChatMode ? 'small-chat-mode' : ''}`}>
        <PageHeader
          user={auth.user}
          wsConnected={isNowWebSocket}
          wsEnabled={wsEnabled}
          onToggleWebsocketTest={handleCancelTransfer}
          isMobile={isMobile}
          isNewChat={isNewChat}
          createNewChat={handleCreateNewChat}
        />
        {isNewChat && isMobile && (
          <div className="new-chat-mobile-top">
            <div className="new-chat-prompt">
              {displayName ? `${displayName} 您好，您想要問什麼？` : '您想要問什麼？'}
            </div>
            <div
              ref={setNewChatStartersMountEl}
              className="new-chat-starters-mount"
            />
          </div>
        )}
        {/* is-positioning：切換到含可操作互動畫面的對話時，先隱藏訊息區、在看不見的狀態下捲到底部並等畫面就緒
            （AGUI-1302，見 useChat.js 的 isPositioning） */}
        <div className={`messages${isPositioning ? ' is-positioning' : ''}`} ref={messagesContainerRef}>
          {messages.map((msg, idx) => (
            <ChatMessage
              // 以訊息 id 為 key（沒有 id 才退回索引），讓互動畫面等有狀態的子元件不會因列表變動而錯置重建
              key={msg.id || `idx-${idx}`}
              message={msg}
              chatId={chatHistory.currentChatId}
              onMessageFeedback={auth.isAuthenticated() ? chatHistory.updateMessageUserFeedback : undefined}
              feedbackDisabled={!auth.isAuthenticated()}
              feedbackQuickOptions={feedbackQuickOptions}
              onImageLoad={scrollToBottom}
              onResend={msg.sender === 'guest' ? resendMessage : undefined}
              // 互動畫面可操作的條件（MCP Apps 規格 §7）：這則訊息之後還沒有任何使用者訊息。
              // 不是看「是不是最後一則」——AI 開啟畫面後若又補一則說明文字，畫面仍可操作。
              mcpAppsInteractive={idx > lastGuestMessageIdx}
              onMcpAppSendMessage={handleMcpAppSendMessage}
              ref={
                (msg.sender === 'chatbot' || msg.sender === 'rocketChat') && idx === messages.length - 1
                  ? lastBotMessageRef
                  : idx === lastGuestMessageIdx
                    ? lastUserMessageRef
                    : null
              }
            />
          ))}
          {/* messages 陣列在串流中已內含一則 id 為 __streaming__ 的暫時 bot 訊息（見 useChat.js），
              此處僅在「非串流」情境（例如真人客服轉接中）補上原本的 loading 泡泡，避免重複顯示兩則。
              isAwaitingReply：只要目前對話最後一則是還沒回覆的提問（含補收冷卻中，尚未確定放棄），
              就持續顯示此等待泡泡，避免畫面看似卡死；同一個狀態也傳給 RichTextarea 鎖住輸入
              （2026-08-05 實測發現泡泡在轉卻能送出第二題比不顯示更令人困惑，改為兩者一致，見 useChat.js）。
              isFinalizeTransient：收尾瞬間正式回覆已落地、串流緩衝尚未清除，此時 isLoading 仍為 true 且
              messages 末尾已非 __streaming__，若不排除會讓等待泡泡在回覆下方閃現一格。 */}
          {(isLoading || isAwaitingReply) && !isFinalizeTransient && messages[messages.length - 1]?.id !== '__streaming__' && (
            <ChatMessage
              message={{ sender: 'chatbot', text: '', isLoading: true }}
              ref={lastBotMessageRef}
            />
          )}
          {/* 底部彈性留白：補足「把最新提問捲到頂端」所需的捲動餘裕，高度由 useChat 動態計算
              （回覆長到超過一個畫面時自動歸零，不會留下永久空白） */}
          <div ref={bottomSpacerRef} className="messages-bottom-spacer" aria-hidden="true" />
          <div ref={messagesEndRef} />
        </div>
        {isPositioning && (
          <div className="messages-positioning-status" role="status">
            <span className="upload-spinner" aria-hidden="true" />
            <span>載入對話中…</span>
          </div>
        )}
        
        {isNewChat && !isMobile && (
          <div className="new-chat-prompt">
            {displayName ? `${displayName} 您好，您想要問什麼？` : '您想要問什麼？'}
          </div>
        )}

        <RichTextarea
          input={input}
          setInput={setInput}
          onSendMessage={sendMessage}
          onStopGeneration={stopGeneration}
          isLoading={isLoading}
          isStopping={isStopping}
          isAwaitingReply={isAwaitingReply}
          chatId={chatHistory.currentChatId}
          onEnsureChatSession={ensureChatSession}
          isNewChat={isNewChat}
          isMobile={isMobile}
          newChatStartersMountEl={isNewChat && isMobile ? newChatStartersMountEl : null}
        />
      </div>
      )}
    </div>
  );
}

export default App;
